import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  ImagePlus,
  LoaderCircle,
  Sparkles,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { ContinuationSuggestionsPanel } from "@/components/ai/continuation-suggestions-panel";
import { ContinuityReviewDialog } from "@/components/ai/continuity-review-dialog";
import { ContinuityReviewPanel } from "@/components/ai/continuity-review-panel";
import { StructuralAnalysisDialog } from "@/components/ai/structural-analysis-dialog";
import { ImageGenerationWorkflowDialogs } from "@/components/image-generation/image-generation-workflow-dialogs";
import { StructuralAnalysisPanel } from "@/components/ai/structural-analysis-panel";
import { StructuralAnalysisScopeDialog } from "@/components/ai/structural-analysis-scope-dialog";
import type { AiAccessPromptView } from "@/features/ai";
import type { StructuralAnalysisCalloutView } from "@/features/ai";
import { ChapterEditor } from "@/components/story/chapter-editor";
import { DeleteSideStoryDialog } from "@/components/story/delete-side-story-dialog";
import { MainStoryDocumentEditor } from "@/components/story/main-story-document-editor";
import { MainStorySetupCard } from "@/components/story/main-story-setup-card";
import { SideStoryForm } from "@/components/story/side-story-form";
import { StoryExportButton } from "@/components/story/story-export-button";
import {
  StoryWorkspaceHeader,
  type StoryEditorSaveState,
} from "@/components/story/story-workspace-header";
import { StoryWorkspaceRail } from "@/components/story/story-workspace-rail";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  buildChapterImagePrompt,
  buildImageDraftSessionFromSource,
  buildSideStoryImagePrompt,
  mapImageCitation,
} from "@/features/image-generation";
import { cn } from "@/lib/utils";
import {
  buildImportedChapterElementData,
  buildImportedChapterView,
  formatStoryErrorMessage,
} from "@/features/story/api/storyClient";
import { getStoryExportTemplatesForTier } from "@/features/story/api/storyExportClient";
import {
  useDefaultTopUpOfferCode,
  useRuntimeCreditCost,
  useRuntimeServicePricing,
} from "@/features/pricing/useRuntimePricing";
import {
  calculateChapterPolishCreditCost,
  calculateSelectionEnhanceCreditCost,
} from "@/features/ai/textEnhancementPricing";
import { SELECTION_ENHANCE_MIN_WORDS } from "@/editors/rich-text/selectionEnhance";
import { acceptStoryImportElement } from "@/features/story-import/api/storyImportClient";
import { isStoryImportReviewEditState } from "@/features/story-import/review/importReviewEdit";
import {
  selectActiveChapter,
  selectActiveContinuityIssue,
  selectActiveStructuralAnalysisRun,
  selectActiveSideStory,
  selectStructuralAnalysisHistory,
  useStoryWorkspaceStore,
} from "@/stores/storyWorkspaceStore";
import { projectAiStore } from "@/stores/projectAiStore";
import { useProjectImageStore } from "@/stores/project-image-store";
import { projectShellStore } from "@/stores/projectShellStore";
import { useSubscriptionStore } from "@/stores/subscriptionStore";

function useTranslatedStoryError() {
  const { t } = useTranslation();

  return (error: unknown) => new Error(t(formatStoryErrorMessage(error)));
}

const STORY_RAIL_COLLAPSED_STORAGE_KEY = "story:workspace-rail-collapsed";
const STORY_AI_RAIL_COLLAPSED_STORAGE_KEY = "story:workspace-ai-rail-collapsed";

type StoryAiFeature =
  | "continuation"
  | "continuity"
  | "illustration"
  | "polish"
  | "structural";

const STORY_AI_FEATURES: StoryAiFeature[] = [
  "continuation",
  "continuity",
  "polish",
  "structural",
  "illustration",
];

function readStoryRailCollapsedState() {
  if (typeof window === "undefined") {
    return false;
  }

  return (
    window.localStorage.getItem(STORY_RAIL_COLLAPSED_STORAGE_KEY) === "true"
  );
}

function readStoryAiRailCollapsedState() {
  if (typeof window === "undefined") {
    return false;
  }

  return (
    window.localStorage.getItem(STORY_AI_RAIL_COLLAPSED_STORAGE_KEY) === "true"
  );
}

function normalizeStoryAiFeature(value: string | null): StoryAiFeature {
  switch (value) {
    case "illustration":
    case "continuity":
    case "polish":
    case "structural":
      return value;
    case "continuation":
    default:
      return "continuation";
  }
}

export function StoryWorkspacePage() {
  const { t } = useTranslation();
  const enhanceSelectionPricing = useRuntimeServicePricing("enhance_selection");
  const polishChapterPricing = useRuntimeServicePricing("polish_chapter");
  const continuationSuggestionCreditCost = useRuntimeCreditCost("continuation_suggestion");
  const continuityReviewCreditCost = useRuntimeCreditCost("continuity_review");
  const structuralAnalysisCreditCost = useRuntimeCreditCost("structural_analysis");
  const imageCreditCost = useRuntimeCreditCost("image_generation");
  const defaultTopUpOfferCode = useDefaultTopUpOfferCode();
  const location = useLocation();
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedChapterId = searchParams.get("chapterId");
  const importReviewEditState = useMemo(() => {
    if (
      !projectId ||
      !isStoryImportReviewEditState(location.state) ||
      location.state.projectId !== projectId ||
      location.state.element.elementType !== "chapter"
    ) {
      return null;
    }

    return location.state;
  }, [location.state, projectId]);
  const activeChapter = useStoryWorkspaceStore(selectActiveChapter);
  const activeSideStory = useStoryWorkspaceStore(selectActiveSideStory);
  const applyGeneratedChapterImage = useStoryWorkspaceStore(
    (state) => state.applyGeneratedChapterImage,
  );
  const applyGeneratedSideStoryImage = useStoryWorkspaceStore(
    (state) => state.applyGeneratedSideStoryImage,
  );
  const clearChapterAi = useStoryWorkspaceStore(
    (state) => state.clearChapterAi,
  );
  const clearLimitPrompt = useStoryWorkspaceStore(
    (state) => state.clearLimitPrompt,
  );
  const clearNotice = useStoryWorkspaceStore((state) => state.clearNotice);
  const continuityDeleteBusyId = useStoryWorkspaceStore(
    (state) => state.continuityDeleteBusyId,
  );
  const continuityError = useStoryWorkspaceStore(
    (state) => state.continuityError,
  );
  const continuityIssues = useStoryWorkspaceStore(
    (state) => state.continuityIssues,
  );
  const continuityNoCanonContext = useStoryWorkspaceStore(
    (state) => state.continuityNoCanonContext,
  );
  const continuityGuardOutcome = useStoryWorkspaceStore(
    (state) => state.continuityGuardOutcome,
  );
  const continuityPrompt = useStoryWorkspaceStore(
    (state) => state.continuityPrompt,
  );
  const continuityRequestStatus = useStoryWorkspaceStore(
    (state) => state.continuityRequestStatus,
  );
  const continuityResolutionBusyId = useStoryWorkspaceStore(
    (state) => state.continuityResolutionBusyId,
  );
  const continuityReviewSummary = useStoryWorkspaceStore(
    (state) => state.continuityReviewSummary,
  );
  const continuationDeleteBusyId = useStoryWorkspaceStore(
    (state) => state.continuationDeleteBusyId,
  );
  const continuationError = useStoryWorkspaceStore(
    (state) => state.continuationError,
  );
  const continuationGuardOutcome = useStoryWorkspaceStore(
    (state) => state.continuationGuardOutcome,
  );
  const continuationPrompt = useStoryWorkspaceStore(
    (state) => state.continuationPrompt,
  );
  const continuationRequestStatus = useStoryWorkspaceStore(
    (state) => state.continuationRequestStatus,
  );
  const continuationSuggestions = useStoryWorkspaceStore(
    (state) => state.continuationSuggestions,
  );
  const continuationUsage = useStoryWorkspaceStore(
    (state) => state.continuationUsage,
  );
  const currentAiCreditBalance = useStoryWorkspaceStore(
    (state) => state.currentAiCreditBalance,
  );
  const dismissEnhanceSuggestion = useStoryWorkspaceStore(
    (state) => state.dismissEnhanceSuggestion,
  );
  const dismissPolishSuggestion = useStoryWorkspaceStore(
    (state) => state.dismissPolishSuggestion,
  );
  const enhanceState = useStoryWorkspaceStore((state) => state.enhanceState);
  const createChapter = useStoryWorkspaceStore((state) => state.createChapter);
  const createMainStory = useStoryWorkspaceStore(
    (state) => state.createMainStory,
  );
  const createSideStory = useStoryWorkspaceStore(
    (state) => state.createSideStory,
  );
  const deleteContinuityIssue = useStoryWorkspaceStore(
    (state) => state.deleteContinuityIssue,
  );
  const deleteContinuationSuggestion = useStoryWorkspaceStore(
    (state) => state.deleteContinuationSuggestion,
  );
  const deleteSideStory = useStoryWorkspaceStore(
    (state) => state.deleteSideStory,
  );
  const exportStoryPiece = useStoryWorkspaceStore(
    (state) => state.exportStoryPiece,
  );
  const hydrateChapterAi = useStoryWorkspaceStore(
    (state) => state.hydrateChapterAi,
  );
  const hydrateWorkspace = useStoryWorkspaceStore(
    (state) => state.hydrateWorkspace,
  );
  const isCreatingMainStory = useStoryWorkspaceStore(
    (state) => state.isCreatingMainStory,
  );
  const isCreatingSideStory = useStoryWorkspaceStore(
    (state) => state.isCreatingSideStory,
  );
  const limitPrompt = useStoryWorkspaceStore((state) => state.limitPrompt);
  const mainStory = useStoryWorkspaceStore((state) => state.mainStory);
  const notice = useStoryWorkspaceStore((state) => state.notice);
  const openChapter = useStoryWorkspaceStore((state) => state.openChapter);
  const openMainStory = useStoryWorkspaceStore((state) => state.openMainStory);
  const openSideStory = useStoryWorkspaceStore((state) => state.openSideStory);
  const reorderChapters = useStoryWorkspaceStore(
    (state) => state.reorderChapters,
  );
  const refreshMediaUrls = useStoryWorkspaceStore(
    (state) => state.refreshMediaUrls,
  );
  const saveChapter = useStoryWorkspaceStore((state) => state.saveChapter);
  const saveMainStory = useStoryWorkspaceStore((state) => state.saveMainStory);
  const saveSideStory = useStoryWorkspaceStore((state) => state.saveSideStory);
  const sideStories = useStoryWorkspaceStore((state) => state.sideStories);
  const polishState = useStoryWorkspaceStore((state) => state.polishState);
  const startCreateSideStory = useStoryWorkspaceStore(
    (state) => state.startCreateSideStory,
  );
  const status = useStoryWorkspaceStore((state) => state.status);
  const tier = useStoryWorkspaceStore((state) => state.tier);
  const isRecoveringMedia = useStoryWorkspaceStore(
    (state) => state.isRecoveringMedia,
  );
  const removeChapterImage = useStoryWorkspaceStore(
    (state) => state.removeChapterImage,
  );
  const replaceChapterImage = useStoryWorkspaceStore(
    (state) => state.replaceChapterImage,
  );
  const requestContinuationSuggestions = useStoryWorkspaceStore(
    (state) => state.requestContinuationSuggestions,
  );
  const requestChapterPolish = useStoryWorkspaceStore(
    (state) => state.requestChapterPolish,
  );
  const requestContinuityReview = useStoryWorkspaceStore(
    (state) => state.requestContinuityReview,
  );
  const requestSelectionEnhance = useStoryWorkspaceStore(
    (state) => state.requestSelectionEnhance,
  );
  const resetEnhanceState = useStoryWorkspaceStore(
    (state) => state.resetEnhanceState,
  );
  const resetPolishState = useStoryWorkspaceStore(
    (state) => state.resetPolishState,
  );
  const selectContinuityIssue = useStoryWorkspaceStore(
    (state) => state.selectContinuityIssue,
  );
  const selectedContinuityIssue = useStoryWorkspaceStore(
    selectActiveContinuityIssue,
  );
  const selectedStructuralAnalysisRun = useStoryWorkspaceStore(
    selectActiveStructuralAnalysisRun,
  );
  const selectedContinuityIssueId = useStoryWorkspaceStore(
    (state) => state.selectedContinuityIssueId,
  );
  const selectedStructuralRunId = useStoryWorkspaceStore(
    (state) => state.selectedStructuralRunId,
  );
  const structuralAnalysisHistory = useStoryWorkspaceStore(
    selectStructuralAnalysisHistory,
  );
  const structuralDeleteBusyId = useStoryWorkspaceStore(
    (state) => state.structuralDeleteBusyId,
  );
  const structuralError = useStoryWorkspaceStore(
    (state) => state.structuralError,
  );
  const structuralGuardOutcome = useStoryWorkspaceStore(
    (state) => state.structuralGuardOutcome,
  );
  const structuralPrompt = useStoryWorkspaceStore(
    (state) => state.structuralPrompt,
  );
  const structuralRequestStatus = useStoryWorkspaceStore(
    (state) => state.structuralRequestStatus,
  );
  const structuralStatusBusyId = useStoryWorkspaceStore(
    (state) => state.structuralStatusBusyId,
  );
  const deleteStructuralAnalysisRun = useStoryWorkspaceStore(
    (state) => state.deleteStructuralAnalysisRun,
  );
  const requestStructuralAnalysis = useStoryWorkspaceStore(
    (state) => state.requestStructuralAnalysis,
  );
  const selectStructuralAnalysisRun = useStoryWorkspaceStore(
    (state) => state.selectStructuralAnalysisRun,
  );
  const updateContinuityIssueResolution = useStoryWorkspaceStore(
    (state) => state.updateContinuityIssueResolution,
  );
  const updateStructuralFindingStatus = useStoryWorkspaceStore(
    (state) => state.updateStructuralFindingStatus,
  );
  const uploadSideStoryImage = useStoryWorkspaceStore(
    (state) => state.uploadSideStoryImage,
  );
  const startDraftSession = useProjectImageStore(
    (state) => state.startDraftSession,
  );
  const checkout = useSubscriptionStore((state) => state.checkout);
  const creditBalance = useSubscriptionStore((state) => state.creditBalance);
  const setCreditBalance = useSubscriptionStore(
    (state) => state.setCreditBalance,
  );
  const toTranslatedError = useTranslatedStoryError();
  const [deleteSideStoryOpen, setDeleteSideStoryOpen] = useState(false);
  const [deleteSideStoryBusy, setDeleteSideStoryBusy] = useState(false);
  const [editorSaveError, setEditorSaveError] = useState<string | null>(null);
  const [editorSaveState, setEditorSaveState] =
    useState<StoryEditorSaveState>("idle");
  const [railCollapsed, setRailCollapsed] = useState(() =>
    readStoryRailCollapsedState(),
  );
  const [aiRailCollapsed, setAiRailCollapsed] = useState(() =>
    readStoryAiRailCollapsedState(),
  );
  const [continuityWorkspaceOpen, setContinuityWorkspaceOpen] = useState(false);
  const [structuralScopeOpen, setStructuralScopeOpen] = useState(false);
  const [structuralWorkspaceOpen, setStructuralWorkspaceOpen] = useState(false);
  const [structuralFocus, setStructuralFocus] = useState<{
    chapterId: string;
    text: string;
  } | null>(null);
  const activeAiFeature = normalizeStoryAiFeature(searchParams.get("ai"));
  const setActiveAiFeature = (feature: StoryAiFeature) => {
    const next = new URLSearchParams(searchParams);
    next.set("ai", feature);
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    if (!projectId) {
      return;
    }

    void hydrateWorkspace(projectId);
  }, [hydrateWorkspace, projectId]);

  useEffect(() => {
    setEditorSaveError(null);
    setEditorSaveState("idle");
    setDeleteSideStoryBusy(false);
    setDeleteSideStoryOpen(false);
    setContinuityWorkspaceOpen(false);
    setStructuralScopeOpen(false);
    setStructuralWorkspaceOpen(false);
    resetEnhanceState();
    resetPolishState();
  }, [
    activeSideStory?.id,
    isCreatingSideStory,
    mainStory?.id,
    mainStory?.mode,
    resetEnhanceState,
    resetPolishState,
  ]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    window.localStorage.setItem(
      STORY_RAIL_COLLAPSED_STORAGE_KEY,
      String(railCollapsed),
    );
  }, [railCollapsed]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    window.localStorage.setItem(
      STORY_AI_RAIL_COLLAPSED_STORAGE_KEY,
      String(aiRailCollapsed),
    );
  }, [aiRailCollapsed]);

  useEffect(() => {
    if (!requestedChapterId || mainStory?.mode !== "chapters") {
      return;
    }

    if (
      mainStory.chapters.some((chapter) => chapter.id === requestedChapterId)
    ) {
      openChapter(requestedChapterId);
    }
  }, [mainStory, openChapter, requestedChapterId]);

  useEffect(() => {
    if (!activeChapter?.id || structuralFocus?.chapterId !== activeChapter.id) {
      return;
    }

    const timeout = window.setTimeout(() => {
      setStructuralFocus(null);
    }, 1500);

    return () => window.clearTimeout(timeout);
  }, [activeChapter?.id, structuralFocus]);

  useEffect(() => {
    if (!activeChapter?.id) {
      clearChapterAi();
      return;
    }

    void hydrateChapterAi();
  }, [activeChapter?.id, clearChapterAi, hydrateChapterAi]);

  useEffect(() => {
    if (typeof continuationUsage?.creditsRemaining === "number") {
      setCreditBalance(continuationUsage.creditsRemaining);
    }
  }, [continuationUsage?.creditsRemaining, setCreditBalance]);

  useEffect(() => {
    if (typeof currentAiCreditBalance === "number") {
      setCreditBalance(currentAiCreditBalance);
    }
  }, [currentAiCreditBalance, setCreditBalance]);

  useEffect(() => {
    projectAiStore
      .getState()
      .setActiveChapterContext(
        activeChapter?.id ?? null,
        activeChapter?.title ?? null,
      );
  }, [activeChapter?.id, activeChapter?.title]);

  const importReviewChapter = useMemo(
    () =>
      importReviewEditState && projectId
        ? buildImportedChapterView({
            displayTitle: importReviewEditState.element.displayTitle,
            elementData: importReviewEditState.element.elementData,
            projectId,
            storyId: mainStory?.id ?? importReviewEditState.storyId,
          })
        : null,
    [importReviewEditState, mainStory?.id, projectId],
  );
  const editorChapter = importReviewChapter ?? activeChapter;
  const isImportReviewEditingChapter = Boolean(importReviewChapter);

  const activeSideStoryAttachments = useMemo(
    () => activeSideStory?.attachments ?? [],
    [activeSideStory],
  );

  const editorChapterOrderFlags = useMemo(() => {
    if (!editorChapter || !mainStory || mainStory.mode !== "chapters") {
      return {
        isClosing: false,
        isOpening: false,
      };
    }

    const chapterIndex = mainStory.chapters.findIndex(
      (chapter) => chapter.id === editorChapter.id,
    );

    if (chapterIndex < 0) {
      return {
        isClosing: false,
        isOpening: false,
      };
    }

    return {
      isClosing: chapterIndex === mainStory.chapters.length - 1,
      isOpening: chapterIndex === 0,
    };
  }, [editorChapter, mainStory]);

  const workspaceSectionClassName = useMemo(
    () =>
      cn(
        "grid gap-6",
        !importReviewEditState &&
          (railCollapsed
            ? "xl:grid-cols-[3rem_minmax(0,1fr)] xl:items-start"
            : "xl:grid-cols-[20rem_minmax(0,1fr)] xl:items-start"),
      ),
    [importReviewEditState, railCollapsed],
  );

  const structuralAnalysisAvailability = useMemo(
    () => ({
      eligible: tier === "author",
      reason: tier === "author" ? null : t("story.ai.structural.locked"),
    }),
    [t, tier],
  );

  const continuationStatusLabel = useMemo(() => {
    if (!continuationUsage) {
      return tier === "free"
        ? t("story.ai.continuation.freeUsesRemaining", { count: 5 })
        : t("story.ai.continuation.creditStatus", {
            cost: continuationSuggestionCreditCost,
            remaining: 0,
          });
    }

    if (typeof continuationUsage.usesRemainingToday === "number") {
      return t("story.ai.continuation.freeUsesRemaining", {
        count: continuationUsage.usesRemainingToday,
      });
    }

    return t("story.ai.continuation.creditStatus", {
      cost: continuationUsage.creditCost,
      remaining: continuationUsage.creditsRemaining ?? 0,
    });
  }, [continuationSuggestionCreditCost, continuationUsage, t, tier]);

  const continuityStatusLabel = useMemo(() => {
    if (tier === "free") {
      return t("story.ai.continuity.lockedStatus");
    }

    return t("story.ai.continuity.creditStatus", {
      cost: continuityReviewCreditCost,
      remaining: currentAiCreditBalance ?? creditBalance,
    });
  }, [
    creditBalance,
    continuityReviewCreditCost,
    currentAiCreditBalance,
    t,
    tier,
  ]);

  const structuralStatusLabel = useMemo(() => {
    if (tier !== "author") {
      return t("story.ai.structural.lockedStatus");
    }

    return t("story.ai.structural.creditStatus", {
      cost: structuralAnalysisCreditCost,
      remaining: currentAiCreditBalance ?? creditBalance,
    });
  }, [
    creditBalance,
    currentAiCreditBalance,
    structuralAnalysisCreditCost,
    t,
    tier,
  ]);

  const editorChapterWordCount = useMemo(() => {
    function collectText(node: unknown): string {
      if (!node || typeof node !== "object") {
        return "";
      }

      const richNode = node as { content?: unknown[]; text?: unknown };
      const ownText = typeof richNode.text === "string" ? richNode.text : "";
      const childText = Array.isArray(richNode.content)
        ? richNode.content.map(collectText).join(" ")
        : "";

      return `${ownText} ${childText}`.trim();
    }

    return collectText(editorChapter?.body).split(/\s+/).filter(Boolean).length;
  }, [editorChapter?.body]);

  const polishChapterCreditCost = useMemo(
    () => calculateChapterPolishCreditCost(editorChapterWordCount, polishChapterPricing),
    [editorChapterWordCount, polishChapterPricing],
  );

  const polishStatusLabel = useMemo(() => {
    if (tier === "free") {
      return t("story.ai.polish.lockedStatus");
    }

    return t("story.ai.polish.creditStatus", {
      cost: polishChapterCreditCost,
      remaining: currentAiCreditBalance ?? creditBalance,
    });
  }, [
    creditBalance,
    currentAiCreditBalance,
    polishChapterCreditCost,
    t,
    tier,
  ]);

  const getEnhanceSelectionCreditCost = useCallback(
    (wordCount: number) =>
      calculateSelectionEnhanceCreditCost(wordCount, enhanceSelectionPricing),
    [enhanceSelectionPricing],
  );

  const getEnhanceDisabledReason = useCallback((wordCount: number) => {
    const remaining = currentAiCreditBalance ?? creditBalance;
    const creditCost = getEnhanceSelectionCreditCost(wordCount);
    if (tier === "free") {
      return t("story.ai.textEnhancement.upgradePrompt");
    }

    if (wordCount < SELECTION_ENHANCE_MIN_WORDS) {
      return t("story.ai.enhance.tooShort", {
        count: SELECTION_ENHANCE_MIN_WORDS,
      });
    }

    if (remaining < creditCost) {
      return t("story.ai.textEnhancement.topUpPrompt");
    }

    return null;
  }, [creditBalance, currentAiCreditBalance, getEnhanceSelectionCreditCost, t, tier]);

  const polishDisabledReason = useMemo(() => {
    const remaining = currentAiCreditBalance ?? creditBalance;
    if (tier === "free") {
      return t("story.ai.textEnhancement.upgradePrompt");
    }

    if (editorChapterWordCount < 20) {
      return t("story.ai.polish.tooShort");
    }

    if (remaining < polishChapterCreditCost) {
      return t("story.ai.textEnhancement.topUpPrompt");
    }

    return null;
  }, [
    creditBalance,
    currentAiCreditBalance,
    editorChapterWordCount,
    polishChapterCreditCost,
    t,
    tier,
  ]);

  const handleAiPromptAction = useMemo(
    () =>
      (prompt: AiAccessPromptView | null) => {
        if (!prompt) {
          return;
        }

        if (prompt.kind === "upgrade") {
          void checkout(
            prompt.recommendedTier === "author"
              ? "author_monthly"
              : "crafter_monthly",
            "/projects",
          );
          return;
        }

        if (prompt.kind === "top-up") {
          void checkout(defaultTopUpOfferCode, "/projects");
        }
      },
    [checkout, defaultTopUpOfferCode],
  );

  const availableExportTemplates = useMemo(
    () => (tier ? getStoryExportTemplatesForTier(tier) : []),
    [tier],
  );

  const hasRecoverableMedia = useMemo(() => {
    const chapterNeedsRefresh = Boolean(
      activeChapter?.imagePath && !activeChapter.imageUrl,
    );
    const sideStoryNeedsRefresh = activeSideStoryAttachments.some(
      (attachment) => attachment.path && !attachment.imageUrl,
    );

    return chapterNeedsRefresh || sideStoryNeedsRefresh;
  }, [
    activeChapter?.imagePath,
    activeChapter?.imageUrl,
    activeSideStoryAttachments,
  ]);

  async function handleGenerateChapterImage() {
    if (!projectId || !activeChapter) {
      return;
    }

    const projectImageSettings = await projectShellStore
      .getState()
      .hydrateProjectImageSettings(projectId);

    if (!projectImageSettings) {
      return;
    }

    startDraftSession(
      buildImageDraftSessionFromSource({
        creditCost: imageCreditCost,
        creditsRemaining: creditBalance,
        existingImagePath: activeChapter.imagePath,
        projectId,
        projectImageSettings,
        prompt: buildChapterImagePrompt({
          chapterBody: activeChapter.body,
          chapterTitle: activeChapter.title,
          styleDescription: projectImageSettings.styleDescription,
        }),
        sourceCitations: [
          mapImageCitation({
            reason: "Selected chapter for this prompt.",
            sourceKind: "chapter",
            sourceLabel: activeChapter.title,
            sourceRecordId: activeChapter.id,
          }),
        ],
        sourceId: activeChapter.id,
        sourceKind: "chapter",
        sourceTitle: activeChapter.title,
        tier,
      }),
    );
  }

  async function handleGenerateSideStoryImage() {
    if (!projectId || !activeSideStory) {
      return;
    }

    const projectImageSettings = await projectShellStore
      .getState()
      .hydrateProjectImageSettings(projectId);

    if (!projectImageSettings) {
      return;
    }

    startDraftSession(
      buildImageDraftSessionFromSource({
        creditCost: imageCreditCost,
        creditsRemaining: creditBalance,
        projectId,
        projectImageSettings,
        prompt: buildSideStoryImagePrompt({
          body: activeSideStory.body,
          styleDescription: projectImageSettings.styleDescription,
          summary: activeSideStory.summary,
          title: activeSideStory.title,
        }),
        sourceCitations: [
          mapImageCitation({
            reason: "Selected side story for this prompt.",
            sourceKind: "side_story",
            sourceLabel: activeSideStory.title,
            sourceRecordId: activeSideStory.id,
          }),
        ],
        sourceId: activeSideStory.id,
        sourceKind: "side_story",
        sourceTitle: activeSideStory.title,
        tier,
      }),
    );
  }

  function renderChapterEditor() {
    if (!editorChapter) {
      return null;
    }

    return (
      <ChapterEditor
        chapter={editorChapter}
        focusText={
          structuralFocus?.chapterId === editorChapter.id
            ? structuralFocus.text
            : null
        }
        headerActions={
          !isImportReviewEditingChapter ? (
            <StoryExportButton
              onExport={(templateId) =>
                exportStoryPiece({
                  projectId: projectId!,
                  targetId: editorChapter.id,
                  targetKind: "chapter",
                  templateId,
                })
              }
              targetTitle={editorChapter.title}
              templates={availableExportTemplates}
            />
          ) : undefined
        }
        isOrderClosing={editorChapterOrderFlags.isClosing}
        isOrderOpening={editorChapterOrderFlags.isOpening}
        onGenerateHeader={
          isImportReviewEditingChapter ? undefined : handleGenerateChapterImage
        }
        onRemoveHeaderImage={
          isImportReviewEditingChapter
            ? undefined
            : async () => {
                try {
                  await removeChapterImage(editorChapter.id);
                } catch (error) {
                  throw toTranslatedError(error);
                }
              }
        }
        onSave={async (input) => {
          try {
            if (importReviewEditState) {
              await acceptStoryImportElement({
                editedElementData: buildImportedChapterElementData(
                  input,
                  importReviewEditState.element.elementData,
                ),
                elementId: importReviewEditState.element.id,
                jobId: importReviewEditState.importJobId,
              });
              navigate(importReviewEditState.reviewRoute, { replace: true });
              return;
            }

            await saveChapter(editorChapter.id, input);
          } catch (error) {
            throw toTranslatedError(error);
          }
        }}
        onSaveStateChange={(nextState, nextError) => {
          setEditorSaveState(nextState);
          setEditorSaveError(nextError);
        }}
        onUploadHeaderImage={
          isImportReviewEditingChapter
            ? undefined
            : async (file) => {
                try {
                  await replaceChapterImage(editorChapter.id, file);
                } catch (error) {
                  throw toTranslatedError(error);
                }
              }
        }
        textEnhancement={
          isImportReviewEditingChapter || !projectId
            ? undefined
            : {
                getEnhanceCreditCost: getEnhanceSelectionCreditCost,
                getEnhanceDisabledReason,
                enhanceState,
                onDismissEnhance: dismissEnhanceSuggestion,
                onDismissPolish: dismissPolishSuggestion,
                onPromptAction: handleAiPromptAction,
                onRequestSelectionEnhance: (selection) => {
                  void requestSelectionEnhance({
                    chapterId: editorChapter.id,
                    chapterTitle: editorChapter.title,
                    paragraphContext: selection.paragraphContext,
                    projectId,
                    selectedText: selection.selectedText,
                    selectionFrom: selection.from,
                    selectionTo: selection.to,
                  }).catch(() => undefined);
                },
                onSelectionChanged: () => {
                  if (
                    enhanceState.status === "suggestion_ready"
                    || enhanceState.status === "error"
                  ) {
                    resetEnhanceState();
                  }
                },
                polishState,
              }
        }
      />
    );
  }

  function handleOpenStructuralChapter(callout: StructuralAnalysisCalloutView) {
    setStructuralWorkspaceOpen(false);
    setStructuralFocus({
      chapterId: callout.chapterId,
      text: callout.text,
    });
    openChapter(callout.chapterId);
  }

  function renderSelectedAiRailPanel() {
    switch (activeAiFeature) {
      case "illustration":
        return (
          <section className="space-y-4">
            <div className="space-y-1">
              <p className="text-xs font-semibold uppercase tracking-[0.24em] text-muted-foreground">
                {t("story.attachments.title", { defaultValue: "Attachments" })}
              </p>
              <h3 className="text-xl text-foreground">
                {t("story.attachments.generateIllustration")}
              </h3>
              <p className="text-sm leading-6 text-muted-foreground">
                {t("story.attachments.chapterDescription")}
              </p>
            </div>
            <div className="rounded-[1.15rem] border border-ink/10 bg-[#f6eee1] px-4 py-4 text-sm leading-6 text-muted-foreground">
              {t("story.workspace.illustrationRailHint", {
                defaultValue:
                  "Illustration controls live in the chapter header. Expand the header to generate, upload, or replace the chapter image.",
              })}
            </div>
            <div className="rounded-[1.15rem] border border-dashed border-ink/10 bg-transparent px-4 py-6 text-center text-sm text-muted-foreground">
              {activeChapter?.headerImageUrl ? (
                <img
                  alt={t("story.attachments.chapterImageAlt", {
                    title: activeChapter.title,
                  })}
                  className="mx-auto h-40 w-full max-w-[12rem] rounded-[1rem] object-cover"
                  src={activeChapter.headerImageUrl}
                />
              ) : (
                <div className="inline-flex items-center gap-2">
                  <ImagePlus className="h-4 w-4" />
                  {t("story.attachments.emptyChapterImage")}
                </div>
              )}
            </div>
          </section>
        );
      case "continuity":
        return (
          <ContinuityReviewPanel
            busy={continuityRequestStatus === "loading"}
            errorMessage={continuityError ? t(continuityError) : null}
            guardNotices={continuityGuardOutcome?.notices ?? []}
            issues={continuityIssues}
            noCanonContext={continuityNoCanonContext}
            onOpenWorkspace={() => setContinuityWorkspaceOpen(true)}
            onPromptAction={() => handleAiPromptAction(continuityPrompt)}
            onRunReview={() => {
              void requestContinuityReview().catch(() => undefined);
            }}
            onSelectIssue={selectContinuityIssue}
            prompt={continuityPrompt}
            reviewSummary={continuityReviewSummary}
            selectedIssueId={
              selectedContinuityIssue?.id ?? selectedContinuityIssueId
            }
            statusLabel={continuityStatusLabel}
          />
        );
      case "structural":
        return (
          <StructuralAnalysisPanel
            activeRun={selectedStructuralAnalysisRun}
            availability={structuralAnalysisAvailability}
            busy={structuralRequestStatus === "loading"}
            errorMessage={structuralError ? t(structuralError) : null}
            guardNotices={structuralGuardOutcome?.notices ?? []}
            history={structuralAnalysisHistory}
            onOpenRun={(runId) => {
              selectStructuralAnalysisRun(runId);
            }}
            onOpenWorkspace={() => setStructuralWorkspaceOpen(true)}
            onPromptAction={() => handleAiPromptAction(structuralPrompt)}
            onRunAnalysis={() => setStructuralScopeOpen(true)}
            prompt={structuralPrompt}
            statusLabel={structuralStatusLabel}
          />
        );
      case "polish":
        return (
          <section className="space-y-4">
            <div className="space-y-1">
              <p className="text-xs font-semibold uppercase tracking-[0.24em] text-muted-foreground">
                {t("story.ai.polish.eyebrow")}
              </p>
              <h3 className="text-xl text-foreground">
                {t("story.ai.polish.title")}
              </h3>
              <p className="text-sm leading-6 text-muted-foreground">
                {t("story.ai.polish.description")}
              </p>
            </div>
            <div className="rounded-md border border-ink/10 bg-[#f6eee1] px-4 py-4 text-sm leading-6 text-muted-foreground">
              {polishStatusLabel}
            </div>
            {polishDisabledReason ? (
              <p className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-950">
                {polishDisabledReason}
              </p>
            ) : null}
            {polishState.noImprovements ? (
              <p className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-950">
                {t("story.ai.textEnhancement.noImprovements")}
              </p>
            ) : null}
            {polishState.status === "error" && polishState.error ? (
              <p className="rounded-md border border-destructive/20 bg-destructive/10 p-3 text-sm text-destructive">
                {t(polishState.error)}
              </p>
            ) : null}
            <Button
              className="w-full"
              disabled={
                Boolean(polishDisabledReason) || polishState.status === "loading"
              }
              onClick={() => {
                if (!projectId || !editorChapter) {
                  return;
                }
                void requestChapterPolish({
                  chapterId: editorChapter.id,
                  projectId,
                }).catch(() => undefined);
              }}
              type="button"
            >
              {polishState.status === "loading" ? (
                <LoaderCircle className="h-4 w-4 animate-spin" />
              ) : (
                <Sparkles className="h-4 w-4" />
              )}
              {polishState.status === "loading"
                ? t("story.ai.polish.loading")
                : t("story.ai.polish.action")}
            </Button>
          </section>
        );
      case "continuation":
      default:
        return (
          <ContinuationSuggestionsPanel
            busy={continuationRequestStatus === "loading"}
            deleteBusyId={continuationDeleteBusyId}
            errorMessage={continuationError ? t(continuationError) : null}
            guardNotices={continuationGuardOutcome?.notices ?? []}
            onPromptAction={() => handleAiPromptAction(continuationPrompt)}
            onDeleteSuggestionSet={(suggestionSetId) => {
              void deleteContinuationSuggestion(suggestionSetId).catch(
                () => undefined,
              );
            }}
            onRequestSuggestions={() => {
              void requestContinuationSuggestions().catch(() => undefined);
            }}
            prompt={continuationPrompt}
            sessions={continuationSuggestions}
            statusLabel={continuationStatusLabel}
          />
        );
    }
  }

  function renderChapterAiRail() {
    if (aiRailCollapsed) {
      return (
        <aside className="flex items-start justify-center border-l border-ink/10 pl-2 pt-2 xl:sticky xl:top-28 xl:h-[calc(100vh-8rem)]">
          <div className="flex items-center gap-1">
            <button
              aria-label={t("story.workspace.expandAiRail", {
                defaultValue: "Expand AI workspace",
              })}
              className="inline-flex h-8 items-center justify-center px-1 text-muted-foreground transition-colors hover:text-foreground"
              onClick={() => setAiRailCollapsed(false)}
              type="button"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="[writing-mode:vertical-rl] text-[10px] font-semibold uppercase tracking-[0.24em] text-muted-foreground">
              AI
            </span>
          </div>
        </aside>
      );
    }

    return (
      <aside className="min-h-0 border-l border-ink/10 pl-5 xl:sticky xl:top-28 xl:h-[calc(100vh-8rem)] xl:max-h-[calc(100vh-8rem)] xl:overflow-hidden">
        <div className="flex h-full min-h-0 flex-col gap-4">
          <div className="flex items-start justify-between gap-3">
            <div className="space-y-1">
              <p className="text-xs font-semibold uppercase tracking-[0.24em] text-muted-foreground">
                {t("story.workspace.aiWorkspace", {
                  defaultValue: "AI workspace",
                })}
              </p>
              <h2 className="font-display text-2xl leading-none tracking-tight text-foreground">
                {t(`story.ai.${activeAiFeature}.title`, {
                  defaultValue:
                    activeAiFeature.charAt(0).toUpperCase()
                    + activeAiFeature.slice(1),
                })}
              </h2>
            </div>
            <button
              aria-label={t("story.workspace.collapseAiRail", {
                defaultValue: "Collapse AI workspace",
              })}
              className="inline-flex h-8 items-center justify-center px-1 text-muted-foreground transition-colors hover:text-foreground"
              onClick={() => setAiRailCollapsed(true)}
              type="button"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>

          <nav
            aria-label={t("story.ai.navigationLabel")}
            className="grid grid-cols-2 gap-2 xl:grid-cols-1"
          >
            {STORY_AI_FEATURES.map((feature) => {
              const selected = feature === activeAiFeature;

              return (
                <button
                  aria-pressed={selected}
                  className={cn(
                    "inline-flex min-h-10 items-center justify-center rounded-md border px-3 py-2 text-sm font-medium transition-colors",
                    selected
                      ? "border-clay bg-[#f6eee1] text-foreground"
                      : "border-ink/10 bg-white/70 text-muted-foreground hover:border-clay/40 hover:text-foreground",
                  )}
                  key={feature}
                  onClick={() => setActiveAiFeature(feature)}
                  type="button"
                >
                  {t(`story.ai.${feature}.tab`, {
                    defaultValue:
                      feature.charAt(0).toUpperCase() + feature.slice(1),
                  })}
                </button>
              );
            })}
          </nav>

          <div className="min-h-0 flex-1 overflow-y-auto pr-1">
            {renderSelectedAiRailPanel()}
          </div>
        </div>
      </aside>
    );
  }

  if (!projectId) {
    return (
      <Card>
        <CardContent className="p-8">
          <p className="text-base text-muted-foreground">
            {t("projects.dashboard.missing")}
          </p>
        </CardContent>
      </Card>
    );
  }

  if (status === "loading" && !mainStory && sideStories.length === 0) {
    return (
      <Card>
        <CardContent className="p-8">
          <p className="text-base text-muted-foreground">
            {t("story.workspace.loading")}
          </p>
        </CardContent>
      </Card>
    );
  }

  if (status === "error" && !mainStory && sideStories.length === 0) {
    return (
      <Card>
        <CardContent className="space-y-4 p-8">
          <div className="space-y-2">
            <p className="text-sm font-semibold uppercase tracking-[0.22em] text-clay">
              {t("story.workspace.eyebrow")}
            </p>
            <h2 className="text-3xl text-foreground">
              {t("story.workspace.unavailableTitle")}
            </h2>
          </div>
          <p className="max-w-3xl text-base leading-7 text-muted-foreground">
            {notice ? t(notice.message) : t("story.messages.loadError")}
          </p>
        </CardContent>
      </Card>
    );
  }

  if (!mainStory) {
    return (
      <div className="project-shell-page">
        {notice ? (
          <div
            className="project-shell-notice"
            role={notice.kind === "error" ? "alert" : "status"}
          >
            <div className="flex items-start justify-between gap-3">
              <p>{t(notice.message)}</p>
              <button
                className="text-muted-foreground transition-colors hover:text-foreground"
                onClick={clearNotice}
                type="button"
              >
                {t("projects.actions.dismiss")}
              </button>
            </div>
          </div>
        ) : null}

        <MainStorySetupCard
          busy={isCreatingMainStory}
          onCreate={async (mode) => {
            try {
              await createMainStory(mode);
            } catch (error) {
              throw toTranslatedError(error);
            }
          }}
        />
      </div>
    );
  }

  return (
    <div className="project-shell-page">
      {notice ? (
        <div
          className="project-shell-notice"
          role={notice.kind === "error" ? "alert" : "status"}
        >
          <div className="flex items-start justify-between gap-3">
            <p>{t(notice.message)}</p>
            <button
              className="text-muted-foreground transition-colors hover:text-foreground"
              onClick={clearNotice}
              type="button"
            >
              {t("projects.actions.dismiss")}
            </button>
          </div>
        </div>
      ) : null}

      {limitPrompt ? (
        <div
          className="rounded-[1.75rem] border border-amber-300/60 bg-amber-50/90 px-5 py-4 text-sm text-amber-950 shadow-vellum"
          role="status"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="space-y-1">
              <p className="font-semibold">
                {t("story.messages.limitReachedTitle")}
              </p>
              <p>
                {t("story.messages.limitReachedBody", {
                  allowed: limitPrompt.allowedCount,
                  current: limitPrompt.currentCount,
                  recommendedTier: limitPrompt.recommendedTier,
                  tier: limitPrompt.tier,
                })}
              </p>
            </div>
            <button
              className="text-amber-700 transition-colors hover:text-amber-900"
              onClick={clearLimitPrompt}
              type="button"
            >
              {t("projects.actions.dismiss")}
            </button>
          </div>
        </div>
      ) : null}

      {hasRecoverableMedia ? (
        <div
          className="rounded-[1.75rem] border border-sky-200/70 bg-sky-50/90 px-5 py-4 text-sm text-sky-950 shadow-vellum"
          role="status"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="space-y-1">
              <p className="font-semibold">
                {t("story.messages.mediaRecoveryTitle")}
              </p>
              <p>{t("story.messages.mediaRecoveryBody")}</p>
            </div>
            <Button
              disabled={isRecoveringMedia}
              onClick={() => {
                void refreshMediaUrls();
              }}
              size="sm"
              type="button"
              variant="outline"
            >
              {isRecoveringMedia
                ? t("story.messages.mediaRecoveryLoading")
                : t("story.messages.mediaRecoveryRetry")}
            </Button>
          </div>
        </div>
      ) : null}

      {importReviewEditState ? (
        <div
          className="rounded-[1.75rem] border border-emerald-200/70 bg-emerald-50/90 px-5 py-4 text-sm text-emerald-950 shadow-vellum"
          role="status"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="space-y-1">
              <p className="font-semibold">
                {t("storyImport.actions.editBeforeAccept")}
              </p>
              <p>{t("storyImport.review.body")}</p>
            </div>
            <Button
              onClick={() =>
                navigate(importReviewEditState.reviewRoute, { replace: true })
              }
              size="sm"
              type="button"
              variant="outline"
            >
              {t("storyImport.actions.backToImport")}
            </Button>
          </div>
        </div>
      ) : null}

      <section className={workspaceSectionClassName}>
        {!importReviewEditState ? (
          <StoryWorkspaceRail
            activeChapterId={activeChapter?.id ?? null}
            activeSideStoryId={activeSideStory?.id ?? null}
            allowCreate={!importReviewEditState}
            collapsed={railCollapsed}
            isCreatingSideStory={isCreatingSideStory}
            mainStory={mainStory}
            onCreateChapter={async () => {
              try {
                await createChapter();
              } catch (error) {
                throw toTranslatedError(error);
              }
            }}
            onCreateSideStory={startCreateSideStory}
            onOpenChapter={openChapter}
            onOpenMainStory={openMainStory}
            onOpenSideStory={openSideStory}
            onReorderChapters={reorderChapters}
            onToggleCollapsed={() => setRailCollapsed((current) => !current)}
            sideStories={sideStories}
          />
        ) : null}

        <div className="min-w-0 space-y-6">
          {isCreatingSideStory ? (
            <SideStoryForm
              mode="create"
              onCancelCreate={openMainStory}
              onSave={async (input) => {
                try {
                  await createSideStory(input);
                } catch (error) {
                  throw toTranslatedError(error);
                }
              }}
              onSaveStateChange={(nextState, nextError) => {
                setEditorSaveState(nextState);
                setEditorSaveError(nextError);
              }}
              story={null}
            />
          ) : null}

          {!isCreatingSideStory && activeSideStory ? (
            <>
              <SideStoryForm
                headerActions={
                  <StoryExportButton
                    onExport={(templateId) =>
                      exportStoryPiece({
                        projectId,
                        targetId: activeSideStory.id,
                        targetKind: "side_story",
                        templateId,
                      })
                    }
                    targetTitle={activeSideStory.title}
                    templates={availableExportTemplates}
                  />
                }
                mode="edit"
                onDelete={() => {
                  setDeleteSideStoryOpen(true);
                }}
                onGenerateImage={handleGenerateSideStoryImage}
                onSave={async (input) => {
                  try {
                    await saveSideStory(activeSideStory.id, {
                      body: input.body,
                      canonStatus: input.canonStatus,
                      summary: input.summary,
                      title: input.title,
                    });
                  } catch (error) {
                    throw toTranslatedError(error);
                  }
                }}
                onSaveStateChange={(nextState, nextError) => {
                  setEditorSaveState(nextState);
                  setEditorSaveError(nextError);
                }}
                onUploadImage={async (file) => {
                  try {
                    await uploadSideStoryImage(activeSideStory.id, file);
                  } catch (error) {
                    throw toTranslatedError(error);
                  }
                }}
                story={activeSideStory}
              />
              <DeleteSideStoryDialog
                onConfirm={() => {
                  setDeleteSideStoryBusy(true);
                  void deleteSideStory(activeSideStory.id)
                    .then(() => {
                      setDeleteSideStoryOpen(false);
                    })
                    .finally(() => {
                      setDeleteSideStoryBusy(false);
                    });
                }}
                onOpenChange={(open) => {
                  if (!deleteSideStoryBusy) {
                    setDeleteSideStoryOpen(open);
                  }
                }}
                open={deleteSideStoryOpen}
                storyTitle={activeSideStory.title}
                submitting={deleteSideStoryBusy}
              />
            </>
          ) : null}

          {!isCreatingSideStory &&
          !activeSideStory &&
          mainStory.mode === "continuous" ? (
            <>
              <StoryWorkspaceHeader
                description={
                  mainStory.summary || t("story.workspace.mainStoryDescription")
                }
                modeLabel={t(`story.modes.${mainStory.mode}`)}
                saveError={editorSaveError}
                saveState={editorSaveState}
                title={mainStory.title}
              />
              <MainStoryDocumentEditor
                onSave={async (content) => {
                  try {
                    await saveMainStory(content);
                  } catch (error) {
                    throw toTranslatedError(error);
                  }
                }}
                onSaveStateChange={(nextState, nextError) => {
                  setEditorSaveState(nextState);
                  setEditorSaveError(nextError);
                }}
                story={mainStory}
              />
            </>
          ) : null}

          {!isCreatingSideStory &&
          !activeSideStory &&
          mainStory.mode === "chapters" &&
          editorChapter ? (
            <>
              <div
                className={
                  isImportReviewEditingChapter
                    ? "grid gap-6"
                    : aiRailCollapsed
                      ? "grid gap-6 xl:grid-cols-[minmax(0,1fr)_2.75rem]"
                      : "grid gap-6 xl:grid-cols-[minmax(0,1fr)_20rem]"
                }
              >
                <div>{renderChapterEditor()}</div>
                {!isImportReviewEditingChapter ? renderChapterAiRail() : null}
              </div>

              {!isImportReviewEditingChapter ? (
                <StructuralAnalysisScopeDialog
                  busy={structuralRequestStatus === "loading"}
                  chapters={mainStory.chapters}
                  creditCost={structuralAnalysisCreditCost}
                  onOpenChange={setStructuralScopeOpen}
                  onSubmit={requestStructuralAnalysis}
                  open={structuralScopeOpen}
                />
              ) : null}

            </>
          ) : null}

          {!isCreatingSideStory &&
          !activeSideStory &&
          mainStory.mode === "chapters" &&
          !editorChapter ? (
            <Card className="project-shell-panel">
              <CardContent className="space-y-4 p-8">
                <div className="space-y-2">
                  <p className="text-sm font-semibold uppercase tracking-[0.22em] text-clay">
                    {t("story.workspace.eyebrow")}
                  </p>
                  <h2 className="text-3xl text-foreground">
                    {t("story.workspace.emptyChapterTitle")}
                  </h2>
                </div>
                <p className="max-w-3xl text-base leading-7 text-muted-foreground">
                  {t("story.workspace.emptyChapterBody")}
                </p>
                <Button
                  onClick={() => {
                    void createChapter().catch(() => undefined);
                  }}
                  type="button"
                >
                  {t("story.workspace.createFirstChapter")}
                </Button>
              </CardContent>
            </Card>
          ) : null}
        </div>
      </section>
      <ImageGenerationWorkflowDialogs
        onApproved={async (result) => {
          if (activeSideStory && result.approvedAssetRef) {
            await applyGeneratedSideStoryImage(
              activeSideStory.id,
              result.approvedAssetRef,
              result.creditsRemaining ?? null,
            );
            return;
          }

          if (activeChapter && result.approvedAssetRef) {
            await applyGeneratedChapterImage(
              activeChapter.id,
              result.approvedAssetRef,
              result.creditsRemaining ?? null,
            );
          }
        }}
      />
      {!importReviewEditState && mainStory.mode === "chapters" ? (
        <>
          <ContinuityReviewDialog
            busy={continuityRequestStatus === "loading"}
            deleteBusyId={continuityDeleteBusyId}
            errorMessage={continuityError ? t(continuityError) : null}
            guardNotices={continuityGuardOutcome?.notices ?? []}
            issues={continuityIssues}
            noCanonContext={continuityNoCanonContext}
            onDeleteIssue={(issueId) => {
              void deleteContinuityIssue(issueId).catch(() => undefined);
            }}
            onOpenChange={setContinuityWorkspaceOpen}
            onPromptAction={() => handleAiPromptAction(continuityPrompt)}
            onRunReview={() => {
              void requestContinuityReview().catch(() => undefined);
            }}
            onSelectIssue={selectContinuityIssue}
            onStatusChange={(issueId, resolutionStatus) => {
              void updateContinuityIssueResolution(
                issueId,
                resolutionStatus,
              ).catch(() => undefined);
            }}
            open={continuityWorkspaceOpen}
            prompt={continuityPrompt}
            resolutionBusyId={continuityResolutionBusyId}
            reviewSummary={continuityReviewSummary}
            selectedIssueId={
              selectedContinuityIssue?.id ?? selectedContinuityIssueId
            }
            statusLabel={continuityStatusLabel}
          />
          <StructuralAnalysisDialog
            activeRunId={selectedStructuralRunId}
            availability={structuralAnalysisAvailability}
            busy={structuralRequestStatus === "loading"}
            deleteBusy={
              structuralDeleteBusyId === selectedStructuralAnalysisRun?.id
            }
            errorMessage={structuralError ? t(structuralError) : null}
            guardNotices={structuralGuardOutcome?.notices ?? []}
            history={structuralAnalysisHistory}
            onDeleteRun={(runId) => {
              if (
                !window.confirm(
                  t("story.ai.structural.report.deleteConfirm"),
                )
              ) {
                return;
              }

              void deleteStructuralAnalysisRun(runId).catch(
                () => undefined,
              );
            }}
            onOpenChange={setStructuralWorkspaceOpen}
            onOpenChapter={handleOpenStructuralChapter}
            onOpenRun={selectStructuralAnalysisRun}
            onPromptAction={() => handleAiPromptAction(structuralPrompt)}
            onRunAnalysis={() => {
              setStructuralWorkspaceOpen(false);
              setStructuralScopeOpen(true);
            }}
            onSetReviewState={(
              runId,
              dimensionName,
              findingIndex,
              reviewState,
            ) => {
              void updateStructuralFindingStatus(
                runId,
                dimensionName,
                findingIndex,
                reviewState,
              ).catch(() => undefined);
            }}
            open={structuralWorkspaceOpen}
            prompt={structuralPrompt}
            run={selectedStructuralAnalysisRun}
            statusBusyId={structuralStatusBusyId}
            statusLabel={structuralStatusLabel}
            storyTitle={mainStory.title}
          />
        </>
      ) : null}
    </div>
  );
}
